import { can, formatCode } from '@pl/domain';
import type { CountBoxDto, CountListItemDto, CountResultDto, DiscrepancyDto, SiteDto } from '@pl/domain';
import { type ReactNode, useId, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { canceledByWho, canceledWords, cancelErrorText, countSubject, mayCancelAt, useCancelCount } from '../../lib/cancelCount';
import { stateWords } from '../../lib/format';
import { useCount, useDiscrepancies, useMe, useSiteMap, useSites, useSkuMap } from '../../lib/queries';
import { useTodaysCounts } from '../../lib/todaysCounts';
import { Chip, Empty, ErrorNote, RefetchNote, SiteTag, SkuTag, Spinner } from '../../ui/bits';
import { Modal } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import { useSiteScope } from '../useSiteScope';
import { siteWhen } from '../when';
import { ResolveActions, resolvedWords, sinceWords } from './discrepancyKit';
import s from './Counts.module.css';

/**
 * Today's counts at each site, the result of the one chosen, and every open discrepancy.
 *
 * The office is no public screen: blindness is the phone's job and the Wall's, so a finished count's
 * result card shows all four figures. What stays blind here is an open count, which has no result
 * anywhere until it is finished. Resolving is a lead's call; everyone else sees the actions marked
 * "Lead only". So is canceling a count in progress, which records nothing and makes no discrepancy.
 */
export function Counts() {
  const { siteId } = useSiteScope();
  const [params] = useSearchParams();
  const chosen = params.get('count') ?? undefined;
  const sites = useSites();
  const shown = (sites.data ?? []).filter((site) => siteId === undefined || site.id === siteId);

  return (
    <div className={s.page}>
      <section className={`panel ${s.today}`} aria-labelledby="counts-today-h">
        <div className="panel-h">
          <h2 id="counts-today-h">Today’s counts</h2>
          <span className="grow" />
          <span className="sub">5 per site</span>
        </div>
        {sites.isLoadingError ? (
          <div className="panel-b stack" style={{ gap: 14, alignItems: 'flex-start' }}>
            <ErrorNote error={sites.error} />
            <button type="button" className="btn" onClick={() => void sites.refetch()}>
              Try again
            </button>
          </div>
        ) : sites.isPending ? (
          <div className="panel-b">
            <Spinner label="Loading sites" />
          </div>
        ) : (
          shown.map((site) => <SiteCounts key={site.id} site={site} chosen={chosen} />)
        )}
      </section>

      {chosen ? (
        <CountCard id={chosen} />
      ) : (
        <div className="panel">
          <Empty title="Choose a count" description="A finished count’s result, and its boxes to resolve, show here." />
        </div>
      )}

      <Queue siteId={siteId} />
    </div>
  );
}

/** One site's day: done, being counted, and still to do — the phone's list, with the office's detail. */
function SiteCounts({ site, chosen }: { site: SiteDto; chosen: string | undefined }) {
  const location = useLocation();
  const me = useMe();
  const skuById = useSkuMap();
  const today = useTodaysCounts(site, { tally: true });
  const mayCancel = mayCancelAt(me.data, site.id);
  const [canceling, setCanceling] = useState<CountListItemDto | null>(null);
  const queries = [today.counts, today.opened, today.suggested];
  const failed = queries.find((q) => q.isLoadingError);
  const loaded = queries.every((q) => q.data !== undefined);
  const lastCounted = useMemo(() => new Map((today.suggested.data ?? []).map((row) => [row.skuId, row.lastCountedAt])), [today.suggested.data]);

  const head = (right: ReactNode) => (
    <div className={s.sh}>
      <SiteTag site={site} />
      <b>{site.name}</b>
      <span className="grow" />
      {right}
    </div>
  );

  if (!site.live)
    return (
      <div className={s.notLive}>
        <SiteTag site={site} />
        Not live · counts start after the stocktake
      </div>
    );
  if (failed)
    return (
      <>
        {head(null)}
        <div className={s.siteErr}>
          <ErrorNote error={failed.error} />
          <button type="button" className="btn sm" onClick={() => queries.forEach((q) => q.isLoadingError && void q.refetch())}>
            Try again
          </button>
        </div>
      </>
    );
  if (!loaded)
    return (
      <>
        {head(<Spinner label={`Loading ${site.name}’s counts`} />)}
      </>
    );

  const link = (c: CountListItemDto) => ({ pathname: '/office/counts', search: withCount(location.search, c.id) });
  const what = (c: CountListItemDto) =>
    c.scope === 'pallet' ? <b className="mono">Pallet {formatCode(c.palletCode ?? '')}</b> : <SkuTag sku={skuById.get(c.skuId ?? '')} short />;
  return (
    <section aria-label={`${site.name} counts`}>
      {head(
        <span className={s.n}>
          {today.done.length} of {today.total} done
        </span>,
      )}
      <ul className={s.cl}>
        {today.done.map((c) => (
          <li key={c.id} className={c.id === chosen ? s.sel : undefined}>
            <Link to={link(c)} className={s.row} aria-current={c.id === chosen ? 'true' : undefined}>
              <SkuTag sku={skuById.get(c.skuId ?? '')} short />
              <span className={s.st}>
                <Chip tone="ok" dot>
                  Done {c.finishedAt ? siteWhen(c.finishedAt, site.timezone) : ''}
                </Chip>
                <small className={c.result && c.result.missing > 0 ? s.bad : undefined}>{outcome(c)}</small>
              </span>
              <span className={s.who}>{c.startedByName}</span>
            </Link>
          </li>
        ))}
        {today.open.map((c) => (
          <li key={c.id} className={`${s.openItem}${c.id === chosen ? ` ${s.sel}` : ''}`}>
            <Link to={link(c)} className={s.row} aria-current={c.id === chosen ? 'true' : undefined}>
              {what(c)}
              <span className={s.st}>
                <Chip>Counting</Chip>
              </span>
              <span className={s.who}>
                {c.startedByName} · {c.scanned} scanned
              </span>
            </Link>
            {mayCancel && (
              <button type="button" className={`btn sm ${s.cancelBtn}`} aria-label={`Cancel count of ${countSubject(c, skuById)}`} onClick={() => setCanceling(c)}>
                Cancel count
              </button>
            )}
          </li>
        ))}
        {today.cancelled.map((c) => (
          <li key={c.id} className={c.id === chosen ? s.sel : undefined}>
            <Link to={link(c)} className={s.row} aria-current={c.id === chosen ? 'true' : undefined}>
              {what(c)}
              <span className={s.st}>
                <Chip>{canceledWords(c)}</Chip>
              </span>
              <span className={s.who}>{canceledByWho(c)}</span>
            </Link>
          </li>
        ))}
        {today.todo.map((skuId) => {
          const last = lastCounted.get(skuId);
          return (
            <li key={skuId}>
              <div className={s.row}>
                <SkuTag sku={skuById.get(skuId)} short />
                <span className={s.st}>
                  <Chip>Not started</Chip>
                </span>
                <span className={s.who}>{last ? `Last counted ${siteWhen(last, site.timezone)}` : 'Never counted'}</span>
              </div>
            </li>
          );
        })}
        {today.total === 0 && <li className={s.none}>Nothing to count here today.</li>}
      </ul>
      <CancelCount count={canceling} site={site} onClose={() => setCanceling(null)} />
    </section>
  );
}

/**
 * "Cancel count": ends a count in progress with no result and no discrepancies, and frees what it
 * held (its SKU's slot, the phones' gate on earlier results, the Wall's blank tiles).
 */
function CancelCount({ count, site, onClose }: { count: CountListItemDto | null; site: SiteDto; onClose: () => void }) {
  const skuById = useSkuMap();
  const cancel = useCancelCount();
  const [reason, setReason] = useState('');
  const reasonId = useId();
  const close = () => {
    setReason('');
    cancel.reset();
    onClose();
  };
  const error = cancelErrorText(cancel.error, site.name);
  return (
    <Modal
      open={count !== null}
      onOpenChange={(open) => !open && !cancel.isPending && close()}
      title="Cancel count"
      description={count ? `Cancel the count of ${countSubject(count, skuById)} at ${site.name}? Nothing is recorded and no discrepancies are made.` : undefined}
      footer={
        <>
          <button type="button" className="btn" disabled={cancel.isPending} onClick={close}>
            Keep it open
          </button>
          <button
            type="button"
            className="btn danger"
            disabled={cancel.isPending}
            onClick={() => count && cancel.mutate({ id: count.id, reason }, { onSuccess: close })}
          >
            Cancel count
          </button>
        </>
      }
    >
      {error && (
        <p role="alert" className="error-note">
          {error}
        </p>
      )}
      <label className="fld" htmlFor={reasonId}>
        <span>Reason (optional)</span>
        <input id={reasonId} className="inp" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
      </label>
    </Modal>
  );
}

const withCount = (search: string, id: string) => {
  const p = new URLSearchParams(search);
  p.set('count', id);
  return `?${p.toString()}`;
};

function outcome(c: CountListItemDto): string {
  if (!c.result) return '';
  if (c.result.missing > 0) return `${c.result.missing} missing`;
  if (c.result.unexpected > 0) return `${c.result.unexpected} unexpected`;
  return 'Matched';
}

/** The chosen count: all four figures once it is finished, and each box's discrepancy. */
function CountCard({ id }: { id: string }) {
  const me = useMe();
  const query = useCount(id);
  const siteById = useSiteMap();
  const skuById = useSkuMap();
  const c = query.data;
  const finished = c?.status === 'finished' && 'missingBoxes' in c ? (c as CountResultDto) : null;
  const discrepancies = useDiscrepancies({ sourceId: id, limit: 200 }, Boolean(finished));
  const byId = useMemo(() => new Map((discrepancies.data ?? []).map((d) => [d.id, d])), [discrepancies.data]);

  if (query.isLoadingError)
    return (
      <div className="panel">
        <div className="panel-b stack" style={{ gap: 14, alignItems: 'flex-start' }}>
          <ErrorNote error={query.error} />
          <button type="button" className="btn" onClick={() => void query.refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  if (!c)
    return (
      <div className="panel panel-b">
        <Spinner label="Loading the count" />
      </div>
    );

  const site = siteById.get(c.siteId);
  const what =
    c.scope === 'pallet' ? <b className={`mono ${s.pallet}`}>Pallet {formatCode(c.palletCode ?? '')}</b> : <SkuTag sku={skuById.get(c.skuId ?? '')} withCode />;
  const header = (meta: string) => (
    <div className={s.resH}>
      {what}
      <SiteTag site={site} />
      <span className="grow" />
      <span className={s.resMeta}>{meta}</span>
    </div>
  );

  if (c.status === 'cancelled')
    return (
      <section className="panel" aria-label="Count result">
        {header(`Canceled ${c.finishedAt ? siteWhen(c.finishedAt, site?.timezone ?? 'UTC') : ''} · ${canceledByWho(c)}`)}
        <Empty
          title={c.cancelledBy === 'end_of_day' ? 'Canceled at the end of the day' : 'Canceled'}
          description={`${'cancelReason' in c && c.cancelReason ? `The reason given: “${c.cancelReason}”. ` : ''}Nothing was recorded and no discrepancies were made.`}
        />
      </section>
    );
  if (!finished)
    return (
      <section className="panel" aria-label="Count result">
        {header(`Started ${siteWhen(c.startedAt, site?.timezone ?? 'UTC')} · ${c.startedByName}`)}
        <Empty title={'scanned' in c ? `Counting now · ${c.scanned} scanned` : 'Counting now'} description="The result shows once the count is finished." />
      </section>
    );

  const r = finished.result!;
  const lead = Boolean(me.data && can(me.data.role, 'resolve_discrepancy'));
  const rows = (boxes: CountBoxDto[]) => boxes.map((box) => <BoxRow key={box.code} box={box} d={box.discrepancyId ? byId.get(box.discrepancyId) : undefined} countSiteId={finished.siteId} />);
  const openOf = (boxes: CountBoxDto[]) => boxes.filter((b) => b.discrepancyId && byId.get(b.discrepancyId)?.status === 'open').length;

  return (
    <section className="panel" aria-label="Count result">
      <RefetchNote queries={[query, discrepancies]} className="panel-b" />
      {header(`Count finished ${finished.finishedAt ? siteWhen(finished.finishedAt, site?.timezone ?? 'UTC') : ''} · ${finished.startedByName}`)}
      <div className={s.big}>
        <div>
          <span className="metric-k">Expected</span>
          <span className="metric-v num">{r.expected}</span>
          <span className={s.metricS}>in stock or picked at {site?.shortCode ?? 'the site'}</span>
        </div>
        <div>
          <span className="metric-k">Found</span>
          <span className="metric-v num">{r.found}</span>
          <span className={s.metricS}>expected and scanned</span>
        </div>
        <div className={r.missing > 0 ? s.bigBad : undefined}>
          <span className="metric-k">Missing</span>
          <span className="metric-v num">{r.missing}</span>
          <span className={s.metricS}>expected, not scanned</span>
        </div>
        <div>
          <span className="metric-k">Unexpected</span>
          <span className="metric-v num">{r.unexpected}</span>
          <span className={s.metricS}>scanned, recorded elsewhere</span>
        </div>
      </div>
      {finished.missingBoxes.length > 0 && (
        <>
          <div className={s.mh}>
            <span className="sec-k">Missing boxes</span>
            {discrepancies.data && <Chip tone={openOf(finished.missingBoxes) > 0 ? 'bad' : 'ok'}>{openOf(finished.missingBoxes)} open</Chip>}
          </div>
          <ul className={s.boxes}>{rows(finished.missingBoxes)}</ul>
        </>
      )}
      {finished.unexpectedBoxes.length > 0 && (
        <>
          <div className={s.mh}>
            <span className="sec-k">Unexpected boxes</span>
            {discrepancies.data && <Chip tone={openOf(finished.unexpectedBoxes) > 0 ? 'warn' : 'ok'}>{openOf(finished.unexpectedBoxes)} open</Chip>}
          </div>
          <ul className={s.boxes}>{rows(finished.unexpectedBoxes)}</ul>
        </>
      )}
      {!lead && (finished.missingBoxes.length > 0 || finished.unexpectedBoxes.length > 0) && (
        <div className={s.rf}>
          <Icon name="lock" />
          <span>
            You’re signed in as <b>{roleWord(me.data?.role)}</b>. A lead at {site?.name ?? 'the site'} resolves these here or in Scan.
          </span>
        </div>
      )}
    </section>
  );
}

function BoxRow({ box, d, countSiteId }: { box: CountBoxDto; d: DiscrepancyDto | undefined; countSiteId: string }) {
  const skuById = useSkuMap();
  const siteById = useSiteMap();
  const kind = d?.kind ?? 'missing';
  // Where an unexpected box was on the record when it turned up here.
  const recorded =
    (box.state === 'in_stock' || box.state === 'picked') && box.siteId && box.siteId !== countSiteId
      ? `Recorded at ${siteById.get(box.siteId)?.name ?? 'another site'}`
      : box.state === 'labelled'
        ? 'Never received'
        : `Recorded as ${stateWords(box.state).toLowerCase()}`;
  return (
    <li className={s.box} role="group" aria-label={formatCode(box.code)}>
      <div className={s.l1}>
        <span className={`mono ${kind === 'unexpected' ? '' : s.codeBad}`}>{formatCode(box.code)}</span>
        <SkuTag sku={skuById.get(box.skuId ?? '')} short />
        <span className="sub">{kind === 'unexpected' ? recorded : 'Not scanned'}</span>
        <span className="grow" />
        {d && d.status === 'resolved' && <span className={s.done}>{resolvedWords(d)}</span>}
      </div>
      {d && <ResolveActions d={d} />}
    </li>
  );
}

const roleWord = (role: string | undefined) => (role ? role.charAt(0).toUpperCase() + role.slice(1) : 'someone else');

const KIND: Record<DiscrepancyDto['kind'], string> = {
  missing: 'Missing in count',
  unexpected: 'Unexpected in count',
  transit_missing: 'Missing from transfer',
};

/** Every open discrepancy across the sites in view, oldest problems included, with a lead's actions. */
function Queue({ siteId }: { siteId: string | undefined }) {
  const query = useDiscrepancies({ siteId, status: 'open', limit: 200 });
  const siteById = useSiteMap();
  const skuById = useSkuMap();
  const rows = query.data ?? [];

  return (
    <section className={`panel ${s.q}`} aria-labelledby="open-discrepancies-h">
      <div className="panel-h">
        <h2 id="open-discrepancies-h">Open discrepancies</h2>
        {query.data && <Chip tone={rows.length > 0 ? 'bad' : 'ok'}>{rows.length}</Chip>}
        <span className="grow" />
      </div>
      {query.isLoadingError ? (
        <div className="panel-b stack" style={{ gap: 14, alignItems: 'flex-start' }}>
          <ErrorNote error={query.error} />
          <button type="button" className="btn" onClick={() => void query.refetch()}>
            Try again
          </button>
        </div>
      ) : query.isPending ? (
        <div className="panel-b">
          <Spinner label="Loading open discrepancies" />
        </div>
      ) : (
        <>
          <RefetchNote queries={[query]} className="panel-b" />
          <table className={`t ${s.dq}`}>
            <thead>
              <tr>
                <th>Box</th>
                <th>SKU</th>
                <th>Where</th>
                <th>What happened</th>
                <th>Open for</th>
                <th className="r">Next step</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const site = siteById.get(d.siteId);
                return (
                  <tr key={d.id} aria-label={formatCode(d.box.code)}>
                    <td className="mono">{formatCode(d.box.code)}</td>
                    <td>
                      <SkuTag sku={skuById.get(d.box.skuId ?? '')} short />
                    </td>
                    <td>
                      <span className={s.where}>
                        <SiteTag site={site} />
                        {d.kind === 'transit_missing' && <span className="sub">in transit</span>}
                      </span>
                    </td>
                    <td>
                      <div className={s.what}>
                        <b>
                          {KIND[d.kind]}
                          {d.kind === 'transit_missing' && <span className="mono"> {d.sourceLabel}</span>}
                        </b>
                        <small>{siteWhen(d.createdAt, site?.timezone ?? 'UTC')}</small>
                      </div>
                    </td>
                    <td>{sinceWords(d.createdAt)}</td>
                    <td className={s.next}>
                      <ResolveActions d={d} />
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 20, textAlign: 'center' }}>
                    Nothing open. Every box is where the records say.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
